// The ⌘K trigger dressed as a search box (design spec §8.1): 480 px, at most 40 vw, 36 px,
// `--sunken` with a hairline. Not an input: it opens the command palette. On phones it folds to
// a 44 px icon button with the same name.
import { SearchIcon } from "lucide-react";
import type * as React from "react";
import { cn } from "../cn";
import { FOCUS_RING } from "./focus-ring";
import { Kbd } from "./kbd";

function SearchButton({
  placeholder = "Search or jump to…",
  shortcut = "⌘K",
  compact = false,
  className,
  ...props
}: Omit<React.ComponentProps<"button">, "children"> & {
  placeholder?: string;
  shortcut?: string;
  /** Beside the mode tabs: 200 px, and an icon button below 1180 px of top bar. */
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      data-slot="search-button"
      className={cn(
        "flex h-9 w-[480px] max-w-[40vw] min-w-0 shrink items-center gap-2 rounded-md border border-border bg-sunken px-2.5 text-sm text-muted-foreground transition-colors duration-(--dur-fast) ease-out hover:border-strong",
        // From 768 px only: on phones the 44 px icon button below wins.
        compact &&
          "md:w-[200px] md:@max-[1180px]:w-9 md:@max-[1180px]:justify-center md:@max-[1180px]:px-0",
        "max-md:size-11 max-md:max-w-none max-md:justify-center max-md:border-transparent max-md:bg-transparent max-md:px-0 max-md:hover:bg-muted",
        FOCUS_RING,
        className,
      )}
      {...props}
    >
      <SearchIcon aria-hidden="true" className="size-4 shrink-0 text-faint max-md:text-secondary" />
      <span
        className={cn(
          "min-w-0 flex-1 truncate text-left max-md:hidden",
          compact && "@max-[1180px]:hidden",
        )}
      >
        {placeholder}
      </span>
      <Kbd className={cn("max-md:hidden", compact && "@max-[1180px]:hidden")}>{shortcut}</Kbd>
    </button>
  );
}

export { SearchButton };
